/**
 * Shared chart axis / legend label formatters — Arabic month names, ar-SA dates,
 * DB status codes and sentinel department names via page translate helpers (p()).
 */
import { getBrandLocale } from './brandCurrency';

const YYYY_MM_RE = /^(\d{4})-(\d{2})$/;
const ISO_DATE_RE = /^\d{4}-\d{2}-\d{2}/;

const DEPARTMENT_SENTINELS = new Set([
  'unassigned',
  'unknown',
  'non assigné',
  'non assigne',
  'sans affectation',
  'non assignee',
]);

const STATUS_KEY_ALIASES = {
  en_attente: ['leave_pending', 'status_en_attente'],
  pending: ['leave_pending', 'status_en_attente'],
  approuve: ['leave_approved', 'status_approuve', 'status_approved'],
  approuvé: ['leave_approved', 'status_approuve'],
  approved: ['leave_approved', 'status_approved'],
  refuse: ['leave_rejected', 'status_refuse', 'status_rejected'],
  refusé: ['leave_rejected', 'status_refuse'],
  rejected: ['leave_rejected', 'status_rejected'],
  actif: ['status_actif', 'status_active'],
  active: ['status_actif', 'status_active'],
  planifie: ['status_planifie', 'status_planned'],
  planifié: ['status_planifie'],
  planned: ['status_planifie'],
  termine: ['status_termine', 'status_completed'],
  terminé: ['status_termine'],
  completed: ['status_termine'],
  en_cours: ['status_en_cours', 'status_in_progress'],
  in_progress: ['status_en_cours'],
  annule: ['status_annule', 'status_cancelled'],
  annulé: ['status_annule'],
  cancelled: ['status_annule'],
  candidat: ['status_candidat', 'status_new'],
  nouveau: ['status_nouveau', 'status_new'],
  entretien: ['status_entretien', 'status_interview'],
  interview: ['status_entretien'],
  offre: ['status_offre', 'status_offer'],
  offer: ['status_offre'],
};

function resolveLocale(locale) {
  return locale || getBrandLocale('ar');
}

function normalizeToken(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[\s-]+/g, '_');
}

function tryTranslate(translateFn, keys, fallback = '') {
  if (typeof translateFn !== 'function') return fallback;
  for (const key of keys) {
    if (!key) continue;
    const result = translateFn(key, { defaultValue: '' });
    if (result && result !== key) return result;
  }
  return fallback;
}

/** YYYY-MM → Arabic month name (e.g. "يناير 2026"). */
export function formatChartMonthKey(value, options = {}) {
  if (value == null || value === '') return options.fallback ?? '';
  const str = String(value).trim();
  const match = str.match(YYYY_MM_RE);
  if (!match) return str;

  const [, year, month] = match;
  const date = new Date(Number(year), Number(month) - 1, 1);
  if (Number.isNaN(date.getTime())) return str;

  const style = options.style || 'short';
  return date.toLocaleDateString(resolveLocale(options.locale), {
    month: style,
    year: 'numeric',
  });
}

/** ISO date (YYYY-MM-DD) or YYYY-MM → ar-SA formatted label for chart axes. */
export function formatChartDate(value, options = {}) {
  if (value == null || value === '') return '';
  const str = String(value).trim();

  if (YYYY_MM_RE.test(str)) {
    return formatChartMonthKey(str, options);
  }

  const parsed = new Date(str.includes('T') ? str : `${str}T12:00:00`);
  if (Number.isNaN(parsed.getTime())) return str;

  return parsed.toLocaleDateString(resolveLocale(options.locale), {
    year: 'numeric',
    month: options.style === 'long' ? 'long' : 'short',
    day: 'numeric',
    ...options.intlOptions,
  });
}

/** Period label — auto-detects YYYY-MM vs ISO date vs passthrough. */
export function formatChartPeriodLabel(value, translateFn, options = {}) {
  if (value == null || value === '') {
    return options.fallback != null ? options.fallback : '';
  }
  const str = String(value).trim();
  if (YYYY_MM_RE.test(str) || ISO_DATE_RE.test(str)) {
    return YYYY_MM_RE.test(str)
      ? formatChartMonthKey(str, options)
      : formatChartDate(str, options);
  }
  return formatChartCategoryLabel(str, translateFn, options);
}

/** DB department name — translates "Unassigned" / "unknown" sentinels. */
export function formatChartDepartment(name, translateFn, options = {}) {
  const fallback = options.fallback ?? 'غير معيّن';
  if (name == null || String(name).trim() === '') {
    return tryTranslate(translateFn, ['unassigned', 'dept_unassigned'], fallback);
  }
  const normalized = String(name).trim().toLowerCase();
  if (DEPARTMENT_SENTINELS.has(normalized)) {
    return tryTranslate(translateFn, ['unassigned', 'dept_unassigned'], fallback);
  }
  return String(name).trim();
}

/** DB status / enum code → Arabic via p() (status_* / leave_* / aliases). */
export function formatChartDbStatus(code, translateFn, options = {}) {
  if (code == null || code === '') return options.fallback ?? '';
  const normalized = normalizeToken(code);
  const prefix = options.keyPrefix || 'status_';
  const keys = [
    ...(STATUS_KEY_ALIASES[normalized] || []),
    `${prefix}${normalized}`,
    `leave_${normalized}`,
    normalized,
  ];
  const fallback = options.fallback ?? String(code).trim();
  return tryTranslate(translateFn, keys, fallback);
}

/** DB category / operation type → Arabic via p() (op_* / cat_* / status_*). */
export function formatChartCategoryLabel(value, translateFn, options = {}) {
  if (value == null || value === '') {
    return options.fallback ?? tryTranslate(translateFn, ['op_other', 'na'], '—');
  }
  const raw = String(value).trim();
  const normalized = normalizeToken(raw);
  const keys = [
    raw,
    `op_${normalized}`,
    `cat_${normalized}`,
    `category_${normalized}`,
    `status_${normalized}`,
  ];
  const fallback = options.fallback ?? raw;
  return tryTranslate(translateFn, keys, fallback);
}

/** Chart.js category tick callback factory. */
export function chartJsPeriodTick(labels, options = {}) {
  return (_value, index) => {
    const label = labels?.[index];
    return label != null ? formatChartPeriodLabel(label, null, options) : '';
  };
}
